/**
 * Start/end impersonation flow helpers.
 *
 * Refreshes identity in-place rather than forcing a full page reload. Identity
 * changes touch every cached query and selector, but `resetApiState()` + a
 * fresh `/context/` fetch + re-initializing `userContextSlice` covers that
 * without bouncing through the bootstrap TransitionScreen.
 *
 * Storage is driving-account-keyed (see userStorage.ts), so we don't need to
 * back up the real super-admin's selections before swapping identity — their
 * keys live under their own account id and the impersonated user's selections
 * live under theirs. End of impersonation flips the driving account id back
 * and reads from the super-admin's keys again automatically.
 */
import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';
import { AppConfig } from '../../Config';
import { AppDispatch } from '../../store/store';
import { emptySplitApi } from '../../store/emptyApi';
import { organizationContextApi } from '../api/organizationContextApi';
import userContextSlice from '../slices/userContextSlice';
import {
  startImpersonationSession,
  endImpersonationSession,
} from '../api/impersonationApi';
import {
  hydrateImpersonation,
  setImpersonationStarting,
  setImpersonationEnding,
  setImpersonationError,
} from '../slices/impersonationSlice';
import { getUserItem, setUserItem } from './userStorage';

type Navigate = (path: string) => void;

async function refreshIdentityContext(
  dispatch: AppDispatch,
  navigate: Navigate,
  forceRefresh: boolean = false,
): Promise<void> {
  dispatch(userContextSlice.actions.setTransitionState());
  dispatch(emptySplitApi.util.resetApiState());

  const authOptions = forceRefresh ? { forceRefresh: true } : undefined;
  const session = await fetchAuthSession(authOptions);
  const token = session.tokens?.idToken?.toString();
  if (!token) {
    throw new Error('No auth token available');
  }
  localStorage.setItem('cognito_token', token);

  const contextResponse = await axios.get(
    `${AppConfig.api.apiUrl}/api/portal/v1/context/`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );

  const data = contextResponse.data;
  const organizations = data.organizations ?? [];

  // initializeUserContext sets state.userContext.user to the new (possibly
  // impersonated) account, which flips the driving account id used by
  // userStorage. All subsequent getUserItem/setUserItem calls now read/write
  // the new identity's keys.
  dispatch(
    userContextSlice.actions.initializeUserContext({
      idToken: token,
      user: undefined,
      organizations,
      departments: data.departments,
      account: data.account,
    }),
  );
  dispatch(hydrateImpersonation(data.active_impersonation ?? null));

  if (organizations.length === 0) {
    throw new Error('User does not belong to any organizations');
  }

  let selectedOrgId: number;
  if (organizations.length === 1) {
    selectedOrgId = organizations[0].id;
  } else {
    const previouslySelectedOrgId = getUserItem('selected_org_id');
    const match = previouslySelectedOrgId
      ? organizations.find((org: any) => Number(org.id) === Number(previouslySelectedOrgId))
      : null;
    selectedOrgId = match ? Number(previouslySelectedOrgId) : Number(organizations[0].id);
  }
  setUserItem('selected_org_id', selectedOrgId.toString());

  const orgContextResponse = await dispatch(
    organizationContextApi.endpoints.apiPortalV1OrganizationContextRetrieve.initiate(
      { organizationId: selectedOrgId },
    ),
  );
  if (!orgContextResponse.error) {
    dispatch(
      userContextSlice.actions.initializeOrganizationContext(orgContextResponse.data),
    );
  }

  navigate('/app/dashboard');
}

export async function beginImpersonation(
  dispatch: AppDispatch,
  navigate: Navigate,
  targetAccountId: number,
  reason: string,
): Promise<void> {
  dispatch(setImpersonationStarting());
  try {
    await startImpersonationSession(targetAccountId, reason);
    await refreshIdentityContext(dispatch, navigate);
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Failed to start impersonation';
    dispatch(setImpersonationError(msg));
    throw err;
  }
}

export async function endImpersonation(
  dispatch: AppDispatch,
  navigate: Navigate,
  sessionId: number,
): Promise<void> {
  dispatch(setImpersonationEnding());
  try {
    await endImpersonationSession(sessionId);
    // Don't dispatch clearImpersonation() here — refreshIdentityContext below
    // dispatches initializeUserContext (flips user.id back to the real admin)
    // AND hydrateImpersonation(data.active_impersonation ?? null) (clears the
    // impersonation slice) in the correct order. Clearing impersonation here
    // first would leave the impersonated user's id as the driving account id
    // for the ~hundreds-of-ms window while fetchAuthSession + /context/ are
    // in flight, so any concurrent RTK Query call (refetchOnFocus, polling)
    // would read/write localStorage under the wrong account.
    await refreshIdentityContext(dispatch, navigate, true);
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Failed to end impersonation';
    dispatch(setImpersonationError(msg));
    throw err;
  }
}
